import { getHostname } from '../helpers/url';
import { getStage } from '../stage';

export enum AppName {
    CONNECTEED = 'CONNECTEED',
    PORTAIL_CLIENT = 'PORTAIL_CLIENT',
    /**
     * Ecrans internes de Client Hub, nommes **Hub Admin**. Sous-domaine a DEUX
     * labels et non `admin.client-hub` : `getStage()` deduit l'environnement du
     * NOMBRE de labels, donc un troisieme ferait passer la production pour un stage
     * nomme « admin ». La cle de l'enumeration garde son nom, elle est comparee a
     * une portee d'application rendue par `inside-api`.
     */
    CLIENT_BO = 'CLIENT_BO',
    ACCOUNT = 'ACCOUNT',
}

type App = {
    [name in AppName]: {
        subdomain: string;
        local: {
            port: number;
        };
    };
};
// @TODO mv to constants ?
const APPS: App = {
    ACCOUNT: {
        /*
         * **`id` et non `account`, decide le 2026-08-23.**
         *
         * `account.freelance.com` appartient a l'ANCIEN projet, qui y est deploye et
         * qui y reste : le code d'ici est un autre code. Deux applications de compte
         * sur un seul hote, c'est la derniere deployee qui gagne, donc celle-ci prend
         * son propre nom.
         *
         * `id` plutot qu'un nom de la famille `hub-*` : cette application ne depend
         * d'aucun des deux produits, elle est la porte d'entree de tous. Et plutot
         * qu'un verbe comme `login` : elle gere aussi le profil, le mot de passe et
         * le MFA, une fois qu'on est entre.
         *
         * **Attention, UN SEUL LABEL, ET CE N'EST PAS UN GOUT.** `getStage()` deduit
         * l'environnement du NOMBRE de labels de l'hote : `develop.id.freelance.com`
         * en fait quatre, donc le stage lu est « develop ». Un sous-domaine a deux
         * labels en ferait cinq et le stage lu serait faux, ce qui casserait les
         * redirections entre applications et les cles de stockage, qui portent le
         * stage en prefixe.
         *
         * **Attention, CETTE LIGNE EST LA PORTE D'ENTREE DES TROIS APPLICATIONS.**
         * `Setup.tsx` du portail et de Hub Admin envoie ici tout visiteur non
         * authentifie. Elle ne doit donc etre fusionnee qu'apres trois choses, dans
         * cet ordre : l'hote repond, le parametre
         * `/<stage>/INSIDE/ACCOUNT_COGNITO_CALLBACK_URL` porte la nouvelle adresse et
         * la pile Cognito d'`inside-api` a ete redeployee (cette valeur est resolue
         * AU DEPLOIEMENT, elle ne suit pas toute seule). Dans l'autre ordre, on
         * envoie les gens sur un hote qui ne resout pas, juste apres leur mot de
         * passe.
         */
        subdomain: 'id',
        local: {
            port: 5100,
        },
    },
    PORTAIL_CLIENT: {
        /*
         * Client Hub, depuis le renommage du 2026-08-22.
         *
         * **Attention, C'EST CETTE CHAINE QUI FABRIQUE L'URL DE PRODUCTION** :
         * `redirectToApp` en fait `https://client-hub.freelance.com`, et `account`,
         * qui est deploye, y envoie apres la connexion. Releve le jour du renommage,
         * `client.freelance.com` et `develop.client.freelance.com` REPONDENT encore,
         * la ou `client-hub.freelance.com` ne resout pas : deployer `account` avec
         * cette ligne avant que les nouveaux hotes existent enverrait les gens dans
         * le vide, juste apres avoir tape leur mot de passe. Le DNS et la
         * distribution passent d'abord.
         */
        subdomain: 'client-hub',
        local: {
            port: 5101,
        },
    },
    CONNECTEED: {
        subdomain: 'connecteed',
        local: {
            port: 5102,
        },
    },
    CLIENT_BO: {
        /*
         * Hub Admin, sur un domaine FRERE et non enfant de celui du portail : un
         * domaine enfant invite a poser des cookies sur `.client-hub.freelance.com`,
         * que le portail lu par les clients pourrait alors lire.
         *
         * Celui-la ne resout nulle part aujourd'hui, l'ancien non plus : rien n'est
         * deploye de ce cote, donc la bascule ne coute rien ici.
         */
        subdomain: 'hub-admin',
        local: {
            port: 5103,
        },
    },
};

/**
 * L'ADRESSE d'une application, sans y aller.
 *
 * Extraite de `redirectToApp` pour PCG-304 : fabriquer une adresse et s'y rendre sont deux
 * gestes, et le second ne sait pas rendre un `href`. Le retour d'`account` vers son produit
 * doit etre un LIEN et non un bouton, parce qu'une destination d'une autre application est
 * exactement ce qu'on ouvre dans un nouvel onglet ; un `<button>` qui appelle
 * `window.location.replace` ne le permet pas, et efface l'entree precedente de l'historique
 * par-dessus le marche.
 */
export const appUrl = (name: AppName, searchParams?: Record<string, string>) => {
    let url;
    const stage = getStage();

    if (stage === 'prod') {
        url = `https://${APPS[name].subdomain}.freelance.com`;
    } else if (stage === 'local') {
        // local can be localhost:port or IP:port
        url = `http://${getHostname()}:${APPS[name].local.port}`;
    } else {
        // stage can be develop.subdomain.freelance.com or commitNumber.subdomain.freelance.com
        url = `https://${stage}.${APPS[name].subdomain}.freelance.com`;
    }

    if (searchParams) {
        url = `${url}?${new URLSearchParams(searchParams).toString()}`;
    }

    return new URL(url).toString();
};

export const redirectToApp = (name: AppName, searchParams?: Record<string, string>) =>
    /* Le calcul de l'adresse ne vit plus qu'une fois : deux copies auraient derive le jour
       ou un sous-domaine change, et c'est deja arrive une fois avec le renommage du
       2026-08-22. */
    window.location.replace(appUrl(name, searchParams));
